import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  GSTSlab,
  Product,
  ProductCategory,
  ProductSubCategory,
} from './products.model';
import { environment } from '../../../environments/environment';
import { resolveScanCode } from '../../core/utils/scan-code.util';

export interface BarcodeLabelTemplate {
  name: string;
  number: string;
  clientId: string;
  zpl: string;
  description: string;
  isActive: boolean;
  storeName: string;
  // Add an id property if the response includes it upon creation
  id?: string;
}
export interface ProductSearchParams {
  sku?: string;
  plu?: string;
  ean?: string;
  query?: string;
  searchType?: 'sku' | 'plu' | 'ean' | 'query';
  searchValue?: string;
  page?: number;
  limit?: number;
  context?: string;
  locationId?: string;
  /**
   * Optional vendor scope. For context GRN/PO/PRN the backend returns products
   * supplied by this vendor: primary vendor on the product master OR any
   * active Vendor SKU Mapping row (a SKU may be mapped to several vendors).
   */
  vendorId?: string;
  /**
   * B2B Sales: overlay this customer's own wholesale rates on the result
   * (each variant gets `customWholesalePrice`).
   */
  b2bCustomerId?: string;
}

export interface BulkCreateRowError {
  row: number;
  column: string;
  error: string;
}

export interface BulkCreateLookups {
  skuEntryMode: 'MANUAL' | 'AUTO';
  departments: { id: string; name: string; departmentCode: number | null }[];
  categories: {
    id: string;
    name: string;
    departmentsId: string | null;
    categoryCode: number | null;
  }[];
  subCategories: {
    id: string;
    name: string;
    categoryId: string;
    subCategoryCode: number | null;
  }[];
  vendors: { id: string; name: string }[];
  manufacturers: { id: string; name: string }[];
  brands: { id: string; name: string; manufacturerId: string }[];
  vendorManufacturers: { vendorId: string; manufacturerId: string }[];
  gstSlabs: { id: string; value: number; displayLabel: string | null }[];
  uoms: string[];
}

export interface BulkCreateValidation {
  skuEntryMode: 'MANUAL' | 'AUTO';
  totalRows: number;
  validRows: number;
  errorRows: number;
  errors: BulkCreateRowError[];
  warnings: BulkCreateRowError[];
}

export interface BulkCreateResult {
  message: string;
  totalRows: number;
  created: number;
  products: { rowNumber: number; sku: string; id: string }[];
  warnings: BulkCreateRowError[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  getGstSlabs() {
    let params = new HttpParams().set('page', 1).set('limit', 50);
    return this.http.get<{ data: Product[]; total: number }>(
      `${environment.gstslabs}`,
      { params }
    );
  }
  getSuppliers() {
    let params = new HttpParams().set('page', 1).set('limit', 50);
    return this.http.get<{ data: Product[]; total: number }>(
      `${environment.suppliers}`,
      { params }
    );
  }
  getCategories() {
    return this.http.get<{ data: Product[]; total: number }>(
      `${environment.productCategories}`
    );
  }

  addDepartment(department: any) {
    return this.http.post(`${environment.apiUrl}/departments`, department);
  }

  getAllDepartments() {
    return this.http.get(`${environment.apiUrl}/departments`);
  }

  getCategoriesByDepartment(departmentId: string) {
    const params = new HttpParams().set('isActive', true);
    return this.http.get(
      `${environment.apiUrl}/departments/${departmentId}/categories`,
      { params }
    );
  }
  addCategory(category: Partial<ProductCategory>) {
    return this.http.post<ProductCategory>(
      `${environment.productCategories}`,
      category
    );
  }
  updateCategory(id: string, category: Partial<ProductCategory>) {
    return this.http.patch<ProductCategory>(
      `${environment.productCategories}/${id}`,
      category
    );
  }

  getSubCategories(categoryId: string) {
    const params = new HttpParams().set('isActive', true);
    return this.http.get<{ data: ProductSubCategory[]; total: number }>(
      `${environment.productCategories}/${categoryId}/sub-categories`,
      { params }
    );
  }

  addSubCategory(subCategory: Partial<ProductSubCategory>) {
    return this.http.post<ProductSubCategory>(
      `${environment.productSubCategories}`,
      subCategory
    );
  }

  updateSubCategory(id: string, subCategory: Partial<ProductSubCategory>) {
    return this.http.patch<ProductSubCategory>(
      `${environment.productSubCategories}/${id}`,
      subCategory
    );
  }
  constructor(private http: HttpClient) {}

  /**
   * Get products with search and pagination
   */
  getProducts(
    pageOptions: { page: number; limit: number },
    searchText: string,
    isConsumable: boolean,
    context?: string
  ): Observable<{ data: Product[]; total: number }> {
    let params = new HttpParams()
      .set('page', pageOptions.page)
      .set('limit', pageOptions.limit)
      .set('query', searchText);

    if (isConsumable !== undefined) {
      params = params.set('isConsumable', String(isConsumable));
    }

    if (context) {
      params = params.set('context', context);
    }

    return this.http.get<{ data: Product[]; total: number }>(
      `${environment.products}/search`,
      { params }
    );
  }

  /**
   * Add a new product
   */
  addProduct(product: Product): Observable<Product> {
    return this.http.post<Product>(`${environment.products}`, product);
  }

  /**
   * Update product - if you need edit functionality later
   */
  updateProduct(id: string, product: Product): Observable<Product> {
    return this.http.patch<Product>(`${environment.products}/${id}`, product);
  }

  getProductById(id: string): Observable<Product> {
    return this.http.get<Product>(`${environment.products}/${id}`);
  }

  getGstRateById(gsiSlabId: string): Observable<GSTSlab> {
    return this.http.get<GSTSlab>(`${environment.gstslabs}/${gsiSlabId}`);
  }

  /**
   * Delete product - if you need delete functionality
   */
  deleteProduct(id: string): Observable<any> {
    return this.http.delete(`${environment.products}/${id}`);
  }

  addVariantPricing(body: any) {
    return this.http.post(`${environment.products}/variant-pricing`, body);
  }

  getProductByBarcode(barcode: string) {
    let params = new HttpParams().set('barcode', barcode);
    return this.http.get(`${environment.products}/search/barcode`, { params });
  }

  /**
   * Resolve a weighable product by the PLU parsed from a scale label barcode.
   * Backed by GET /products/search/plu (isWeighable rows only).
   */
  getProductByPlu(plu: string) {
    let params = new HttpParams().set('plu', plu);
    return this.http.get(`${environment.products}/search/plu`, { params });
  }

  searchByName(query: string, locationId?: string): Observable<Product[]> {
    return this.searchProducts({ query }).pipe(
      map((response) => response.data || [])
    );
  }

  searchByNameWithContext(
    query: string,
    context: string = 'BULK',
    limit?: number
  ): Observable<any[]> {
    return this.searchProducts({ query, context, limit }).pipe(
      map((response) => response.data || [])
    );
  }

  searchProducts(
    searchParams: ProductSearchParams
  ): Observable<{ data: Product[]; total: number }>;
  searchProducts(
    term: string,
    searchType: 'sku' | 'plu' | 'ean' | 'query',
    locationId?: string,
    context?: string
  ): Observable<{ data: Product[]; total: number }>;
  searchProducts(
    searchParamsOrTerm: ProductSearchParams | string,
    searchType?: 'sku' | 'plu' | 'ean' | 'query',
    locationId?: string,
    context?: string
  ): Observable<{ data: Product[]; total: number }> {
    let params = new HttpParams();

    // Handle overloaded method signatures
    if (typeof searchParamsOrTerm === 'string') {
      // Old method signature: searchProducts(term, searchType, locationId, context)
      const searchParams: ProductSearchParams = {
        [searchType === 'query' ? 'query' : searchType!]: searchParamsOrTerm,
        locationId,
        context,
      };
      return this.searchProducts(searchParams);
    }

    // New method signature: searchProducts(searchParams)
    const searchParams = { ...searchParamsOrTerm };

    // Scan hygiene (core/utils/scan-code.util). The Search-by-EAN boxes take
    // scanner input, and a 2D imager may hand over a pack's marketing QR (a
    // URL) instead of the EAN. That gets an empty result right here — no
    // request, no partial LIKE on the server. A GS1 Digital Link / element
    // string is searched by its GTIN instead.
    const scanned =
      searchParams.ean ??
      (searchParams.searchType === 'ean' ? searchParams.searchValue : undefined);
    if (scanned) {
      const scan = resolveScanCode(scanned);
      if (scan.kind === 'ignored' && scan.reason !== 'empty') {
        return of({ data: [], total: 0 });
      }
      if (scan.kind === 'gtin') {
        if (searchParams.ean) searchParams.ean = scan.code;
        else searchParams.searchValue = scan.code;
      }
    }

    // Handle pagination parameters
    if (searchParams.page) {
      params = params.set('page', searchParams.page.toString());
    }
    if (searchParams.limit) {
      params = params.set('limit', searchParams.limit.toString());
    }

    // Handle main search parameters with individual if statements
    if (searchParams.sku) {
      params = params.set('sku', searchParams.sku);
    }
    if (searchParams.plu) {
      params = params.set('plu', searchParams.plu);
    }
    if (searchParams.ean) {
      params = params.set('ean', searchParams.ean);
    }
    if (searchParams.context) {
      params = params.set('context', searchParams.context);
    }
    if (searchParams.locationId) {
      params = params.set('locationId', searchParams.locationId);
    }
    if (searchParams.vendorId) {
      params = params.set('vendorId', searchParams.vendorId);
    }
    if (searchParams.b2bCustomerId) {
      params = params.set('b2bCustomerId', searchParams.b2bCustomerId);
    }
    if (searchParams.query) {
      params = params.set('query', searchParams.query);
    }

    // Handle generic search parameters
    if (searchParams.searchType && searchParams.searchValue) {
      if (searchParams.searchType === 'query') {
        params = params.set('query', searchParams.searchValue);
      } else {
        params = params.set(searchParams.searchType, searchParams.searchValue);
      }
    }

    return this.http
      .get<{ data: Product[]; total: number }>(
        `${environment.products}/search`,
        { params }
      )
      .pipe(
        catchError((error) => {
          console.error('Product search error:', error);
          return of({ data: [], total: 0 });
        })
      );
  }

  // Generic search with a plain object (no interfaces)
  genericProductSearch(paramsObj: any = {}): Observable<any> {
    // Same scan hygiene as searchProducts for the plain-object callers.
    for (const key of ['ean', 'barcode'] as const) {
      const v = paramsObj?.[key];
      if (typeof v !== 'string' || !v) continue;
      const scan = resolveScanCode(v);
      if (scan.kind === 'ignored' && scan.reason !== 'empty') {
        return of({ data: [], total: 0 });
      }
      if (scan.kind === 'gtin') paramsObj = { ...paramsObj, [key]: scan.code };
    }
    let params = new HttpParams();
    Object.entries(paramsObj).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      if (Array.isArray(v))
        v.forEach((i) => (params = params.append(k, String(i))));
      else if (typeof v === 'object')
        Object.entries(v as any).forEach(([kk, vv]) => {
          if (vv !== undefined && vv !== null && vv !== '') {
            params = params.append(`${k}.${kk}`, String(vv));
          }
        });
      else params = params.set(k, String(v));
    });

    return this.http.get<any>(`${environment.products}/search`, { params });
  }

  // Quick helper methods
  searchBySKU(sku: string): Observable<Product[]> {
    return this.searchProducts({ sku }).pipe(
      map((response) => response.data || [])
    );
  }

  searchBySKUWithContext(
    sku: string,
    context: string = 'BULK',
    vendorId?: string
  ): Observable<any[]> {
    return this.searchProducts({ sku, context, vendorId }).pipe(
      map((response) => response.data || [])
    );
  }

  searchByPLU(plu: string): Observable<Product[]> {
    return this.searchProducts({ plu }).pipe(
      map((response) => response.data || [])
    );
  }

  searchByEAN(ean: string): Observable<Product[]> {
    return this.searchProducts({ ean }).pipe(
      map((response) => response.data || [])
    );
  }

  genericSearch(
    searchType: 'sku' | 'plu' | 'ean' | 'query',
    searchValue: string
  ): Observable<Product[]> {
    return this.searchProducts({ searchType, searchValue }).pipe(
      map((response) => response.data || [])
    );
  }

  generateNextSKU(body: {
    departmentId: string;
    categoryId: string;
    subCategoryId: string;
  }): Observable<{ sku: string }> {
    return this.http.post<{ sku: string }>(
      `${environment.products}/sku/generate-next`,
      body
    );
  }

  searchProductWithContextTansferOut(
    query: string,
    locationId: string,
    context: string = 'TRANSFER_OUT'
  ) {
    const params = new HttpParams()
      .set('query', query)
      .set('locationId', locationId)
      .set('context', context);

    return this.http.get(`${environment.products}/search`, {
      params,
    });
  }

  prnSearch(
    vendorId: string,
    sku?: string,
    plu?: string,
    ean?: string,
    query?: string
  ): Observable<Product[]> {
    let params = new HttpParams().set('vendorId', vendorId);

    if (sku) params = params.set('sku', sku);
    if (plu) params = params.set('plu', plu);
    if (ean) params = params.set('ean', ean);
    if (query) params = params.set('query', query);

    return this.http
      .get<{ data: Product[] }>(`${environment.products}/prn-search`, {
        params,
      })
      .pipe(
        map((res) => res.data || []),
        catchError((err) => {
          console.error('PRN search error', err);
          return of([]);
        })
      );
  }

  updateProductStatus(id: string, isActive: boolean): Observable<Product> {
    return this.http.patch<Product>(`${environment.products}/${id}/active`, {
      isActive,
    });
  }

  getBarCodeLabelTemplates() {
    console.log(
      `Getting bar code label templates...${JSON.stringify(environment)}`
    );
    return this.http.get(`${environment.apiUrl}/barcode-lables/templates`);
  }

  // printBarCodeLabels(
  //   templateId: string,
  //   products: Product[],
  //   quantity: number
  // ) {
  //   return this.http.post(`${environment.products}/barcode-labels`, {
  //     templateId,
  //     products,
  //     quantity,
  //   });
  // }

  // // ----------------------------------------------------------------------
  // // NEW METHOD: Create a ZPL label template (POST /barcode-lables/templates)
  // // ----------------------------------------------------------------------

  // /**
  //  * Create a new ZPL label template.
  //  * @param templateData The data for the new barcode label template.
  //  * @returns An Observable of the created BarcodeLabelTemplate object.
  //  */
  // addBarCodeLabelTemplate(
  //   templateData: BarcodeLabelTemplate
  // ): Observable<BarcodeLabelTemplate> {
  //   // The endpoint is '/barcode-lables/templates' which is relative to the API base.
  //   // Based on your existing structure, we use environment.products as the base path.
  //   const url = `${environment.products}/barcode-labels/templates`;

  //   return this.http.post<BarcodeLabelTemplate>(url, templateData).pipe(
  //     catchError((error) => {
  //       console.error('Error creating barcode label template:', error);
  //       // You might want to re-throw the error or return a specific observable
  //       throw error;
  //     })
  //   );
  // }

  printBarCode(payload: any): Observable<any> {
    // Assuming the print endpoint is POST /barcode-labels/print or similar
    // Check your backend docs for the correct endpoint. Using a placeholder here.
    return this.http.post(
      `${environment.apiUrl}/barcode-lables/print`,
      payload
    );
  }
  getAllSubcategories(): any {
    // 1. Define the required boolean query parameter
    let params = new HttpParams();

    // We expect a boolean, and many APIs require it to be passed as a string
    params = params.append('isActive', 'true');
    // OR params = params.set('active', 'true');

    // 2. Pass the parameters object in the get request
    return this.http.get(`${environment.apiUrl}/product-sub-categories`, {
      params: params,
    });
  }

  // Download product template
  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${environment.products}/template`, {
      responseType: 'blob',
    });
  }

  // Bulk upload products (Creation Flow)
  bulkUpload(formData: FormData): Observable<any> {
    return this.http.post(`${environment.products}/bulk-upload`, formData);
  }

  // Dry-run of the creation file: row errors + masters that would be
  // auto-created (departments, categories, brands, ...). No writes.
  bulkUploadPreview(formData: FormData): Observable<any> {
    return this.http.post(
      `${environment.products}/bulk-upload/preview`,
      formData,
    );
  }

  // Render a list of upload errors as an Excel file to fix from.
  exportBulkUploadErrors(errors: any[]): Observable<Blob> {
    return this.http.post(
      `${environment.products}/bulk-upload/errors/export`,
      { errors },
      { responseType: 'blob' },
    );
  }

  // Download product update template (With SKU)
  downloadUpdateTemplate(): Observable<Blob> {
    return this.http.get(`${environment.products}/template/update`, {
      responseType: 'blob',
    });
  }

  // Bulk update products (Update Flow)
  bulkUpdateUpload(formData: FormData): Observable<any> {
    return this.http.post(
      `${environment.products}/bulk-upload/update`,
      formData
    );
  }

  // ----------------------------------------------------------------------
  // Bulk Add Products (grid) — JSON sibling of the Excel creation flow
  // ----------------------------------------------------------------------

  /** Mint the next internal EAN-13 from the client's prefix sequence. */
  getNextEan(): Observable<{ clientId: string; eanCode: string }> {
    return this.http.get<{ clientId: string; eanCode: string }>(
      `${environment.products}/ean/next`,
    );
  }

  /** Masters, GST slabs, UOMs and the SKU entry mode, in one request. */
  getBulkCreateLookups(): Observable<BulkCreateLookups> {
    return this.http.get<BulkCreateLookups>(
      `${environment.products}/bulk-create/lookups`,
    );
  }

  /** Dry run: per-cell errors, nothing written. */
  validateBulkCreate(rows: any[]): Observable<BulkCreateValidation> {
    return this.http.post<BulkCreateValidation>(
      `${environment.products}/bulk-create/validate`,
      { rows },
    );
  }

  /** Create every row or nothing at all. */
  bulkCreateProducts(rows: any[]): Observable<BulkCreateResult> {
    return this.http.post<BulkCreateResult>(
      `${environment.products}/bulk-create`,
      { rows },
    );
  }

  // ----------------------------------------------------------------------
  // Category Bulk Operations
  // ----------------------------------------------------------------------

  // Download Category Template
  downloadCategoryTemplate(): Observable<Blob> {
    return this.http.get(`${environment.productCategories}/template`, {
      responseType: 'blob',
    });
  }

  // Bulk Upload Categories
  bulkUploadCategories(formData: FormData): Observable<any> {
    return this.http.post(
      `${environment.productCategories}/bulk-upload`,
      formData
    );
  }

  // Download Classification Data (Dept/Cat/SubCat)
  downloadClassificationData(): Observable<Blob> {
    return this.http.get(`${environment.products}/classification/download`, {
      responseType: 'blob',
    });
  }

  // Download All Product/SKU Master
  downloadProductMaster(): Observable<Blob> {
    return this.http.get(`${environment.products}/export/master`, {
      responseType: 'blob',
    });
  }
}
